import { Popover } from '@/components/utils/Popover/Popover';
import { capture } from '@/handlers/error';
import { SlideComponentCharacterModel, SlideComponentCharacterSoundGroupModel, SlideComponentCharacterSoundModel } from '@/types/slide';
import { useLazyQuery } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, FormControl, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNotifications } from '@toolpad/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { GET_CHARACTER_PHRASES, GetCharacterPhrasesResponse, GetCharacterPhrasesVariables } from './gql';

const SIZE = {
    width: 440,
    height: 640,
};

type WordPickerProps = {
    characterList?: SlideComponentCharacterModel[];
    characterSound?: SlideComponentCharacterSoundModel;
    phrase: string;
    phraseSound?: SlideComponentCharacterSoundModel;
    phraseSoundGroup?: SlideComponentCharacterSoundGroupModel;
};

interface Props {
    type: 'phrase' | 'word' | 'card';
    title: string;
    anchorEl: HTMLElement | null;
    value: WordPickerProps;
    onClose: () => void;
    onUpdate: (settings: WordPickerProps) => void;
    slotProps?: { paper?: { ref: React.Ref<HTMLDivElement> } };
}

export const WordPicker: React.FC<Props> = ({ type, title, anchorEl, onClose, onUpdate, value, slotProps }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();

    const [getCharacterPhrases] = useLazyQuery<GetCharacterPhrasesResponse, GetCharacterPhrasesVariables>(GET_CHARACTER_PHRASES);

    const open = Boolean(anchorEl);

    const [searchText, setSearchText] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState('zh-HK');
    const [searchRes, setSearchRes] = useState<GetCharacterPhrasesResponse['characterPhrases']>();
    const [searching, setSearching] = useState(false);

    const onSearchChange = React.useCallback(
        async (value: string, locale: string) => {
            setSearchText(value);

            if (!value) {
                return;
            }

            setSearching(true);
            try {
                const res = await getCharacterPhrases({
                    variables: {
                        where: {
                            phrase: {
                                contains: value,
                            },
                            character: {
                                some: {
                                    locale: {
                                        equals: locale,
                                    },
                                },
                            },
                        },
                        characterWhere: {
                            locale: {
                                equals: locale,
                            },
                        },
                    },
                });

                if (type === 'word') {
                    setSearchRes(res.data?.characterPhrases.filter((phrase) => phrase.phrase.length === 1));
                } else if (type === 'card') {
                    setSearchRes(
                        res.data?.characterPhrases.filter(
                            (phrase) => phrase.character.length === 1 && (phrase.character[0]?.serial1.length ?? 0 > 0) && (phrase.character[0]?.serial2.length ?? 0 > 0),
                        ),
                    );
                } else {
                    setSearchRes(res.data?.characterPhrases);
                }
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setSearching(false);
            }
        },
        [getCharacterPhrases, notifications, type],
    );

    const debouncedSearchChange = useDebounce(onSearchChange, 500);

    return (
        <Popover
            title={title}
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
                onClose();
            }}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: -8,
            }}
            slotProps={{
                paper: {
                    ref: slotProps?.paper?.ref,
                    sx: {
                        width: SIZE.width,
                        height: SIZE.height,
                        display: 'flex',
                        p: 2,
                    },
                },
            }}
        >
            <Stack spacing={1.5}>
                <TextField
                    label={t('slide.components.assets.image.search')}
                    placeholder={t('slide.components.assets.image.search')}
                    autoFocus
                    fullWidth
                    onChange={(e) => {
                        debouncedSearchChange(e.target.value, selectedLanguage);
                    }}
                    defaultValue={searchText}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            inputProps: {
                                ...((type === 'word' || type === 'card') && { maxLength: 1 }),
                            },
                        },
                    }}
                />

                <FormControl sx={{ display: 'flex', alignItems: 'center' }}>
                    <ToggleButtonGroup
                        value={selectedLanguage}
                        exclusive
                        fullWidth
                        onChange={(_e, value) => {
                            if (value) {
                                debouncedSearchChange(searchText, value);
                                setSelectedLanguage(value);
                            }
                        }}
                        sx={{ justifyContent: 'space-evenly' }}
                    >
                        <ToggleButton value='zh-HK' fullWidth>
                            {t('slide.components.picker.word.traditionalChinese')}
                        </ToggleButton>
                        <ToggleButton value='zh-CN' fullWidth>
                            {t('slide.components.picker.word.simplifiedChinese')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </Stack>

            <Box
                sx={(theme) => ({
                    mt: 2,
                    flex: 1,
                    display: 'flex',
                    alignContent: 'center',
                    overflow: 'auto',
                    backgroundColor: (theme.vars || theme).palette.grey[200],
                    ...theme.applyStyles('dark', {
                        backgroundColor: (theme.vars || theme).palette.grey[700],
                    }),
                })}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        display: 'flex',
                        px: 1,
                        my: 1.5,
                    }}
                >
                    {!searchText ? (
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                            <SearchIcon fontSize='small' />
                            <Typography>{t('slide.components.picker.word.placeholder')}</Typography>
                        </Box>
                    ) : searching ? (
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : searchRes && searchRes.length <= 0 ? (
                        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                            <Typography>{t('slide.components.assets.image.noResultFor', { keyword: searchText })}</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1 }}>
                            <Grid container spacing={1} sx={{ justifyContent: 'space-evenly' }}>
                                {searchRes &&
                                    searchRes.map((item) => (
                                        <Grid
                                            key={item.id}
                                            sx={{
                                                position: 'relative',
                                                width: 120,
                                                height: 120,
                                                backgroundColor: (theme) => (theme.vars || theme).palette.background.default,
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px solid',
                                                borderColor: 'transparent',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                    border: '2px solid',
                                                    borderColor: 'primary.main',
                                                },
                                                p: 1,
                                            }}
                                            onClick={() => {
                                                /**
                                                 * When we fetch character variant characters are always fetched,
                                                 * since we are displaying phrase it doesn't make sense to include variant characters hence filtering them out
                                                 */
                                                const characterList = item.character.filter((character) => item.phrase.includes(character.character));

                                                onUpdate({
                                                    ...value,
                                                    characterList: characterList,
                                                    characterSound: {
                                                        jyutping: item?.character?.[0]?.jyutping ?? [],
                                                        pinyin: item?.character?.[0]?.pinyin ?? [],
                                                    },
                                                    phrase: item.phrase,
                                                    phraseSound: {
                                                        jyutping: item?.jyutping,
                                                        pinyin: item?.pinyin,
                                                    },
                                                    phraseSoundGroup: {
                                                        jyutping: [
                                                            {
                                                                startIndex: 0,
                                                            },
                                                        ],
                                                        pinyin: [
                                                            {
                                                                startIndex: 0,
                                                            },
                                                        ],
                                                    },
                                                });
                                                onClose();
                                            }}
                                        >
                                            <Typography variant='h5'>{item.phrase}</Typography>
                                        </Grid>
                                    ))}

                                {searchRes &&
                                    searchRes.length % 3 !== 0 &&
                                    // Fill the remaining space with empty boxes just to make it looks nice with space-evenly
                                    Array.from({ length: 3 - (searchRes.length % 3) }).map((_, index) => (
                                        <Grid
                                            key={`empty-${index}`}
                                            sx={{
                                                width: 120,
                                                height: 120,
                                                backgroundColor: 'transparent',
                                                borderRadius: 1,
                                            }}
                                        />
                                    ))}
                            </Grid>
                        </Box>
                    )}
                </Box>
            </Box>
        </Popover>
    );
};
